import type { OutlookEmailContext } from '../types'

declare global {
  interface Window {
    Office?: any
  }
}

export function isOfficeAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.Office !== 'undefined'
}

export function isOutlookMailboxAvailable(): boolean {
  return Boolean(window.Office?.context?.mailbox?.item)
}

export async function waitForOfficeReady(timeoutMs = 8000): Promise<boolean> {
  if (!isOfficeAvailable()) return false

  // Office.onReady exists in modern Office.js
  if (typeof window.Office.onReady === 'function') {
    return new Promise<boolean>((resolve) => {
      let done = false
      const timer = setTimeout(() => {
        if (done) return
        done = true
        resolve(false)
      }, timeoutMs)

      window.Office.onReady(() => {
        if (done) return
        done = true
        clearTimeout(timer)
        resolve(true)
      })
    })
  }

  return isOutlookMailboxAvailable()
}

export function isComposeMode(): boolean {
  const item = window.Office?.context?.mailbox?.item
  // Office.js: item.itemType === Office.MailboxEnums.ItemType.Message and item.body exists in both read/compose.
  // Heuristic: in compose there is item.to/cc and item.body.setSelectedDataAsync is writable.
  return Boolean(item?.body?.setSelectedDataAsync)
}

function getItemId(): string | undefined {
  const item = window.Office?.context?.mailbox?.item
  // In read mode, itemId is usually available. In compose, it can be null/undefined until saved.
  return item?.itemId || item?.internetMessageId || undefined
}

function getFromEmail(): string | undefined {
  const item = window.Office?.context?.mailbox?.item
  const from = item?.from
  return from?.emailAddress || from?.address || undefined
}

function extractRecipients(list: any): string[] | undefined {
  if (!Array.isArray(list)) return undefined
  const emails = list
    .map((r) => r?.emailAddress || r?.address)
    .filter((x) => typeof x === 'string' && x.trim().length > 0)
    .map((x) => (x as string).trim())
  return emails.length ? emails : undefined
}

function getToEmails(): string[] | undefined {
  const item = window.Office?.context?.mailbox?.item
  return extractRecipients(item?.to)
}

function getCcEmails(): string[] | undefined {
  const item = window.Office?.context?.mailbox?.item
  return extractRecipients(item?.cc)
}

function getSentAtIso(): string | undefined {
  const item = window.Office?.context?.mailbox?.item
  const dt = item?.dateTimeReceived || item?.dateTimeCreated
  try {
    return dt ? new Date(dt).toISOString() : undefined
  } catch {
    return undefined
  }
}

async function getBodyText(): Promise<string> {
  const item = window.Office?.context?.mailbox?.item
  if (!item?.body?.getAsync) return ''

  return new Promise<string>((resolve, reject) => {
    item.body.getAsync('text', (result: any) => {
      if (result?.status === 'succeeded') resolve(result.value || '')
      else reject(new Error(result?.error?.message || 'Failed to read email body'))
    })
  })
}

export async function fetchCurrentEmailContext(): Promise<OutlookEmailContext> {
  const ready = await waitForOfficeReady()
  if (!ready || !isOutlookMailboxAvailable()) {
    // Browser/mock mode
    return {
      subject: '[Mock] RE: Riverside Bridge Pour – maturity results + schedule confirmation',
      from_email: 'amy.chen@giatecscientific.com',
      to_emails: ['john.miller@acmebuild.com'],
      cc_emails: ['sarah.patel@giatecscientific.com', 'mark.cai@giatecscientific.com', 'pmo@acmebuild.com'],
      sent_at: new Date().toISOString(),
      thread_text:
        'Hi John,\n\nThanks for the updated pour plan. Quick update from our side:\n\n- We validated the latest sensor batch (Batch ID: RSB-2402) and it is ready for site delivery.\n- For the maturity dashboard, we can provide 15-min interval data + daily PDF summary.\n\nCan you confirm the final placement start time (local) and whether you want the summary to include strength estimate + temperature curve?\n\nBest,\nAmy Chen\nCustomer Success | Giatec Scientific\n+1 (555) 012-3456\n\n---\nFrom: John Miller <john.miller@acmebuild.com>\nSent: Tuesday, February 4, 2026 4:18 PM\nTo: Amy Chen <amy.chen@giatecscientific.com>\nCc: Sarah Patel <sarah.patel@giatecscientific.com>; Mark Cai <mark.cai@giatecscientific.com>; PMO <pmo@acmebuild.com>\nSubject: RE: Riverside Bridge Pour – maturity results + schedule confirmation\n\nHi Amy,\n\nAppreciate it. Here are the details for the Riverside Bridge deck pour:\n\n- Pour window: Feb 6 (Fri) 02:00–08:00\n- Target placement start: 02:30\n- Concrete mix: 35 MPa, w/c 0.42, SCM: 20% fly ash\n- Sensors: 6 locations (see attached layout)\n\nRequests:\n1) Please generate a short email to our inspector summarizing what maturity data will be collected.\n2) Also draft a message to our internal team confirming the schedule and asking for any conflicts.\n\nIf possible, keep drafts concise.\n\nThanks,\nJohn\n\nJohn Miller\nProject Manager | ACME Build Co.\n(555) 987-6543\n\nAttachment: RiversideBridge_SensorLayout.pdf\n\n---\nFrom: Mark Cai <mark.cai@giatecscientific.com>\nSent: Tuesday, February 4, 2026 3:52 PM\nTo: Amy Chen <amy.chen@giatecscientific.com>\nSubject: FW: Riverside Bridge – need customer-ready summary\n\nAmy,\n\nCan you reply today with:\n- what we can deliver (dashboard + report)\n- what we need from them (final start time, location contacts)\n\nAlso: please avoid promising compressive strength accuracy beyond what the calibration supports.\n\n-Mark\n\n--\n(You are in mock mode: open this page inside Outlook to read real emails.)',
      item_id: undefined,
      is_compose: false,
    }
  }

  const item = window.Office.context.mailbox.item
  const subject = item?.subject || undefined
  const from_email = getFromEmail()
  const to_emails = getToEmails()
  const cc_emails = getCcEmails()
  const sent_at = getSentAtIso()
  const thread_text = await getBodyText()
  const item_id = getItemId()
  const is_compose = isComposeMode()

  return {
    subject,
    from_email,
    to_emails,
    cc_emails,
    sent_at,
    thread_text: thread_text || '',
    item_id,
    is_compose,
  }
}

export async function insertDraftIntoCompose(draftText: string): Promise<void> {
  const ready = await waitForOfficeReady()
  if (!ready || !isOutlookMailboxAvailable()) {
    throw new Error('Office/Outlook is not available. Open this add-in inside Outlook.')
  }

  const item = window.Office.context.mailbox.item
  if (!item?.body?.setSelectedDataAsync) {
    throw new Error('Insert is only available in compose mode. Click Reply, then try Insert again.')
  }

  return new Promise<void>((resolve, reject) => {
    item.body.setSelectedDataAsync(
      draftText,
      { coercionType: 'text' },
      (result: any) => {
        if (result?.status === 'succeeded') resolve()
        else reject(new Error(result?.error?.message || 'Failed to insert into compose'))
      }
    )
  })
}

